import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import RichContent from '../components/RichContent';
import SimpleRichTextEditor from '../components/SimpleRichTextEditor';
import { useAuth } from '../contexts/AuthContext';
import { blogAPI } from '../services/api';
import type { BlogAsset, BlogPost, BlogPostPayload, BlogPostSummary } from '../types';

type EditorState = {
  title: string;
  subheading: string;
  slug: string;
  tagsInput: string;
  bodyHtml: string;
  coverAsset: BlogAsset | null;
};

const EMPTY_EDITOR: EditorState = {
  title: '',
  subheading: '',
  slug: '',
  tagsInput: '',
  bodyHtml: '',
  coverAsset: null,
};

const BlogAdmin: React.FC = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<BlogPostSummary[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [selectedPostId, setSelectedPostId] = useState<number | 'new' | null>(null);
  const [currentPost, setCurrentPost] = useState<BlogPost | null>(null);
  const [editor, setEditor] = useState<EditorState>(EMPTY_EDITOR);
  const [searchInput, setSearchInput] = useState('');
  const deferredQuery = useDeferredValue(searchInput.trim());
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published'>('all');
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [assetUploading, setAssetUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [needsGate, setNeedsGate] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPostsLoading(true);
    setError(null);

    blogAPI
      .listAdminPosts({
        page: 1,
        pageSize: 50,
        query: deferredQuery,
        status: statusFilter,
      })
      .then(({ data }) => {
        if (!cancelled) {
          setPosts(data.posts);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const apiError = err?.response?.data?.error;
        if (err?.response?.status === 403 && apiError === 'admin_gate_required') {
          setNeedsGate(true);
          return;
        }
        console.error('Failed to load admin blog posts', err);
        setError('Unable to load blog posts.');
      })
      .finally(() => {
        if (!cancelled) setPostsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [deferredQuery, statusFilter]);

  useEffect(() => {
    if (selectedPostId !== null) return;
    if (posts.length > 0) {
      setSelectedPostId(posts[0].id);
    }
  }, [posts, selectedPostId]);

  useEffect(() => {
    if (selectedPostId === 'new') {
      setCurrentPost(null);
      setEditor(EMPTY_EDITOR);
      setPreviewMode(false);
      return;
    }

    if (selectedPostId === null) return;

    let cancelled = false;
    setDetailLoading(true);
    setError(null);

    blogAPI
      .getAdminPost(selectedPostId)
      .then(({ data }) => {
        if (cancelled) return;
        setCurrentPost(data.post);
        setEditor(editorFromPost(data.post));
      })
      .catch((err) => {
        if (cancelled) return;
        const apiError = err?.response?.data?.error;
        if (err?.response?.status === 403 && apiError === 'admin_gate_required') {
          setNeedsGate(true);
          return;
        }
        console.error('Failed to load selected blog post', err);
        setError('Unable to load this post.');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedPostId]);

  const parsedTags = useMemo(() => {
    return editor.tagsInput
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
  }, [editor.tagsInput]);

  const payload = useMemo<BlogPostPayload>(() => ({
    title: editor.title.trim(),
    subheading: editor.subheading.trim(),
    slug: editor.slug.trim(),
    tags: parsedTags,
    body_html: editor.bodyHtml,
    cover_asset_id: editor.coverAsset?.id ?? null,
  }), [editor, parsedTags]);

  const refreshList = async (preferredPostId?: number | 'new') => {
    const response = await blogAPI.listAdminPosts({
      page: 1,
      pageSize: 50,
      query: deferredQuery,
      status: statusFilter,
    });
    setPosts(response.data.posts);
    if (typeof preferredPostId === 'number') {
      setSelectedPostId(preferredPostId);
      return;
    }
    if (preferredPostId === 'new') {
      setSelectedPostId('new');
      return;
    }
    if (response.data.posts.length > 0 && selectedPostId === null) {
      setSelectedPostId(response.data.posts[0].id);
    }
  };

  const handleCreateNew = () => {
    setSelectedPostId('new');
    setCurrentPost(null);
    setEditor(EMPTY_EDITOR);
    setNotice(null);
    setError(null);
    setPreviewMode(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (selectedPostId === 'new' || currentPost === null) {
        const response = await blogAPI.createPost(payload);
        setCurrentPost(response.data.post);
        setEditor(editorFromPost(response.data.post));
        await refreshList(response.data.post.id);
        setNotice('Draft saved.');
      } else {
        const response = await blogAPI.updatePost(currentPost.id, payload);
        setCurrentPost(response.data.post);
        setEditor(editorFromPost(response.data.post));
        await refreshList(response.data.post.id);
        setNotice('Post updated.');
      }
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      if (err?.response?.status === 403 && apiError === 'admin_gate_required') {
        setNeedsGate(true);
      } else {
        setError(apiError || 'Unable to save this post.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePublishToggle = async (nextAction: 'publish' | 'unpublish') => {
    setPublishing(true);
    setError(null);
    setNotice(null);
    try {
      let postId = currentPost?.id;
      if (!postId) {
        const createResponse = await blogAPI.createPost(payload);
        postId = createResponse.data.post.id;
        setCurrentPost(createResponse.data.post);
      } else {
        const saveResponse = await blogAPI.updatePost(postId, payload);
        setCurrentPost(saveResponse.data.post);
      }

      const response =
        nextAction === 'publish'
          ? await blogAPI.publishPost(postId)
          : await blogAPI.unpublishPost(postId);

      setCurrentPost(response.data.post);
      setEditor(editorFromPost(response.data.post));
      await refreshList(response.data.post.id);
      setNotice(nextAction === 'publish' ? 'Post published.' : 'Post moved back to draft.');
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      if (err?.response?.status === 403 && apiError === 'admin_gate_required') {
        setNeedsGate(true);
      } else {
        setError(apiError || `Unable to ${nextAction} this post.`);
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleInlineImageUpload = async (file: File) => {
    setAssetUploading(true);
    setError(null);
    try {
      const response = await blogAPI.uploadAsset(file);
      return response.data.asset.url;
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      setError(apiError || 'Unable to upload image.');
      throw err;
    } finally {
      setAssetUploading(false);
    }
  };

  const handleCoverUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAssetUploading(true);
    setError(null);
    try {
      const response = await blogAPI.uploadAsset(file);
      setEditor((current) => ({
        ...current,
        coverAsset: response.data.asset,
      }));
      setNotice('Cover image uploaded.');
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      setError(apiError || 'Unable to upload cover image.');
    } finally {
      setAssetUploading(false);
      event.target.value = '';
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-900">Admin access required</h1>
          <p className="mt-3 text-sm text-slate-600">
            This section is limited to administrators.
          </p>
        </div>
      </Layout>
    );
  }

  if (needsGate) {
    return (
      <Layout className="max-w-4xl">
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-8 text-amber-950 shadow-sm">
          <h1 className="text-2xl font-semibold">Admin passcode required</h1>
          <p className="mt-3 text-sm leading-6">
            Unlock the admin session first, then come back to the blog CMS.
          </p>
          <Link
            to="/grandslam"
            className="mt-5 inline-flex rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white"
          >
            Go to Admin Dashboard
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout className="max-w-[96rem] py-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-700">Admin CMS</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Blog Manager</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/grandslam"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back to Admin
          </Link>
          <button
            type="button"
            onClick={handleCreateNew}
            className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
          >
            New Post
          </button>
        </div>
      </div>

      {(error || notice) && (
        <div
          className={`mt-6 rounded-2xl px-5 py-4 text-sm ${
            error ? 'border border-rose-200 bg-rose-50 text-rose-700' : 'border border-emerald-200 bg-emerald-50 text-emerald-700'
          }`}
        >
          {error || notice}
        </div>
      )}

      <div className="mt-8 grid gap-6 xl:grid-cols-[22rem_minmax(0,1fr)]">
        <aside className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="space-y-4">
            <div>
              <label htmlFor="blog-admin-search" className="text-sm font-medium text-slate-700">
                Search posts
              </label>
              <input
                id="blog-admin-search"
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search title or content"
                className="mt-2 w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-100"
              />
            </div>

            <div>
              <label htmlFor="blog-admin-status" className="text-sm font-medium text-slate-700">
                Status
              </label>
              <select
                id="blog-admin-status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as 'all' | 'draft' | 'published')}
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-100"
              >
                <option value="all">All posts</option>
                <option value="draft">Drafts</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {postsLoading ? (
              <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
                Loading posts...
              </div>
            ) : posts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
                No posts found.
              </div>
            ) : (
              posts.map((post) => {
                const isSelected = selectedPostId === post.id;
                return (
                  <button
                    key={post.id}
                    type="button"
                    onClick={() => setSelectedPostId(post.id)}
                    className={`w-full rounded-2xl border px-4 py-4 text-left transition ${
                      isSelected
                        ? 'border-sky-600 bg-sky-50 shadow-sm'
                        : 'border-slate-200 bg-slate-50/60 hover:border-slate-300 hover:bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-900">{post.title}</span>
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${
                          post.status === 'published'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-slate-200 text-slate-600'
                        }`}
                      >
                        {post.status}
                      </span>
                    </div>
                    {post.subheading && (
                      <p className="mt-2 text-sm leading-6 text-slate-600">{post.subheading}</p>
                    )}
                    <p className="mt-3 text-xs font-medium uppercase tracking-[0.16em] text-slate-400">
                      {post.author.name} · {formatAdminDate(post.updated_at)}
                    </p>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">
                {currentPost?.status === 'published' ? 'Published post' : 'Draft'}
              </p>
              <h2 className="mt-1 text-2xl font-semibold text-slate-950">
                {selectedPostId === 'new' || !currentPost ? 'New Post' : currentPost.title}
              </h2>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setPreviewMode((current) => !current)}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {previewMode ? 'Edit' : 'Preview'}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || publishing}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {saving ? 'Saving...' : currentPost?.status === 'published' ? 'Save Edits' : 'Save Draft'}
              </button>
              <button
                type="button"
                onClick={() => handlePublishToggle(currentPost?.status === 'published' ? 'unpublish' : 'publish')}
                disabled={saving || publishing}
                className={`rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                  currentPost?.status === 'published'
                    ? 'bg-rose-600 hover:bg-rose-500'
                    : 'bg-sky-700 hover:bg-sky-600'
                }`}
              >
                {publishing
                  ? currentPost?.status === 'published'
                    ? 'Unpublishing...'
                    : 'Publishing...'
                  : currentPost?.status === 'published'
                    ? 'Unpublish'
                    : 'Publish'}
              </button>
            </div>
          </div>

          {detailLoading ? (
            <div className="py-16 text-center text-sm text-slate-500">Loading editor...</div>
          ) : previewMode ? (
            <div className="space-y-6 py-6">
              <div>
                <p className="text-sm font-medium uppercase tracking-[0.16em] text-slate-500">
                  Preview
                </p>
                <h2 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950">
                  {editor.title || 'Untitled post'}
                </h2>
                {editor.subheading && (
                  <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">
                    {editor.subheading}
                  </p>
                )}
              </div>

              {editor.coverAsset?.url && (
                <img
                  src={editor.coverAsset.url}
                  alt={editor.title || 'Cover image preview'}
                  className="aspect-[16/8] w-full rounded-[1.5rem] object-cover"
                />
              )}

              <RichContent html={editor.bodyHtml} />
            </div>
          ) : (
            <div className="space-y-6 py-6">
              <div className="grid gap-5 lg:grid-cols-2">
                <div>
                  <label htmlFor="blog-title" className="text-sm font-medium text-slate-700">
                    Title
                  </label>
                  <input
                    id="blog-title"
                    type="text"
                    value={editor.title}
                    onChange={(event) => setEditor((current) => ({ ...current, title: event.target.value }))}
                    className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-100"
                  />
                </div>

                <div>
                  <label htmlFor="blog-slug" className="text-sm font-medium text-slate-700">
                    Slug
                  </label>
                  <input
                    id="blog-slug"
                    type="text"
                    value={editor.slug}
                    onChange={(event) => setEditor((current) => ({ ...current, slug: event.target.value }))}
                    placeholder="Leave blank to auto-generate from title"
                    className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-100"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="blog-subheading" className="text-sm font-medium text-slate-700">
                  Subheading
                </label>
                <textarea
                  id="blog-subheading"
                  value={editor.subheading}
                  onChange={(event) => setEditor((current) => ({ ...current, subheading: event.target.value }))}
                  rows={3}
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-100"
                />
              </div>

              <div>
                <label htmlFor="blog-tags" className="text-sm font-medium text-slate-700">
                  Tags
                </label>
                <input
                  id="blog-tags"
                  type="text"
                  value={editor.tagsInput}
                  onChange={(event) => setEditor((current) => ({ ...current, tagsInput: event.target.value }))}
                  placeholder="Comma-separated, for example: bidding, pricing, municipalities"
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-100"
                />
                {parsedTags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {parsedTags.map((tag) => (
                      <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Cover image</p>
                    <p className="mt-1 text-sm text-slate-500">
                      Optional, but strongly recommended for the blog feed and article header.
                    </p>
                  </div>
                  <label className="inline-flex cursor-pointer rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50">
                    {assetUploading ? 'Uploading...' : 'Upload cover image'}
                    <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
                  </label>
                </div>

                {editor.coverAsset?.url && (
                  <div className="mt-5 overflow-hidden rounded-[1.25rem] border border-slate-200 bg-white">
                    <img
                      src={editor.coverAsset.url}
                      alt={editor.title || 'Cover image'}
                      className="aspect-[16/8] w-full object-cover"
                    />
                    <div className="flex items-center justify-between px-4 py-3">
                      <p className="text-sm text-slate-500">{editor.coverAsset.filename}</p>
                      <button
                        type="button"
                        onClick={() => setEditor((current) => ({ ...current, coverAsset: null }))}
                        className="text-sm font-medium text-rose-600 hover:text-rose-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Body</label>
                <div className="mt-2">
                  <SimpleRichTextEditor
                    value={editor.bodyHtml}
                    onChange={(nextValue) => setEditor((current) => ({ ...current, bodyHtml: nextValue }))}
                    onUploadImage={handleInlineImageUpload}
                  />
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};

function editorFromPost(post: BlogPost): EditorState {
  return {
    title: post.title || '',
    subheading: post.subheading || '',
    slug: post.slug || '',
    tagsInput: (post.tags || []).join(', '),
    bodyHtml: post.body_html || '',
    coverAsset: post.cover_image || null,
  };
}

function formatAdminDate(value?: string | null): string {
  if (!value) return 'No date';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default BlogAdmin;
